import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { savedOpportunityAPI } from '../services/api';
import { Contract } from '../types';
import { trackEvent } from '../utils/analytics';
import { PENDING_SAVE_KEY } from '../constants/savedOpportunities';

type SaveSurface = 'table' | 'card' | 'detail' | 'saved_page' | 'auth_handoff';

type ToastState = {
  message: string;
  action: 'view' | 'undo';
  contractId: number;
} | null;

interface SavedOpportunitiesContextType {
  isSaved: (opportunity: Pick<Contract, 'id' | 'is_saved'>) => boolean;
  isSaving: (contractId: number) => boolean;
  toggleSaved: (opportunity: Contract, surface: SaveSurface) => Promise<void>;
  setSavedState: (contractId: number, saved: boolean) => void;
  toast: ToastState;
  dismissToast: () => void;
  undoToastAction: () => Promise<void>;
}

const SavedOpportunitiesContext = createContext<SavedOpportunitiesContextType | undefined>(undefined);

// Consumers share this hook with the provider module, matching the app's context pattern.
// eslint-disable-next-line react-refresh/only-export-components
export const useSavedOpportunities = () => {
  const context = useContext(SavedOpportunitiesContext);
  if (!context) throw new Error('useSavedOpportunities must be used within SavedOpportunitiesProvider');
  return context;
};

export const SavedOpportunitiesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [overrides, setOverrides] = useState<Record<number, boolean>>({});
  const [pendingIds, setPendingIds] = useState<Set<number>>(new Set());
  const [toast, setToast] = useState<ToastState>(null);

  const setSavedState = useCallback((contractId: number, saved: boolean) => {
    setOverrides((current) => ({ ...current, [contractId]: saved }));
  }, []);

  const persist = useCallback(async (contractId: number, saved: boolean, surface: SaveSurface) => {
    setPendingIds((current) => new Set(current).add(contractId));
    setSavedState(contractId, saved);
    trackEvent('opportunity_save_attempted', { contract_id: contractId, surface, action: saved ? 'save' : 'unsave' });
    try {
      if (saved) {
        await savedOpportunityAPI.save(contractId);
        trackEvent('opportunity_saved', { contract_id: contractId, surface });
        setToast({ message: 'Opportunity saved.', action: 'view', contractId });
      } else {
        await savedOpportunityAPI.unsave(contractId);
        trackEvent('opportunity_unsaved', { contract_id: contractId, surface });
        setToast({ message: 'Removed from Saved Opportunities.', action: 'undo', contractId });
      }
    } catch (error) {
      setSavedState(contractId, !saved);
      setToast({ message: 'Unable to update this opportunity. Please try again.', action: 'view', contractId });
      trackEvent('save_failed', { contract_id: contractId, surface, action: saved ? 'save' : 'unsave' });
      throw error;
    } finally {
      setPendingIds((current) => {
        const next = new Set(current);
        next.delete(contractId);
        return next;
      });
    }
  }, [setSavedState]);

  useEffect(() => {
    if (!user) {
      setOverrides({});
      return;
    }
    const raw = window.localStorage.getItem(PENDING_SAVE_KEY);
    if (!raw) return;
    try {
      const pending = JSON.parse(raw) as { contractId?: number };
      if (pending.contractId) {
        window.localStorage.removeItem(PENDING_SAVE_KEY);
        void persist(pending.contractId, true, 'auth_handoff').then(() => {
          trackEvent('save_auth_completed', { contract_id: pending.contractId });
        }).catch(() => undefined);
      }
    } catch {
      window.localStorage.removeItem(PENDING_SAVE_KEY);
    }
  }, [persist, user]);

  const isSaved = useCallback((opportunity: Pick<Contract, 'id' | 'is_saved'>) => {
    if (!opportunity.id) return false;
    return overrides[opportunity.id] ?? Boolean(opportunity.is_saved);
  }, [overrides]);

  const isSaving = useCallback((contractId: number) => pendingIds.has(contractId), [pendingIds]);

  const toggleSaved = useCallback(async (opportunity: Contract, surface: SaveSurface) => {
    if (!opportunity.id || !user || pendingIds.has(opportunity.id)) return;
    await persist(opportunity.id, !isSaved(opportunity), surface);
  }, [isSaved, pendingIds, persist, user]);

  const undoToastAction = useCallback(async () => {
    if (!toast || toast.action !== 'undo') return;
    const contractId = toast.contractId;
    setToast(null);
    await persist(contractId, true, 'saved_page');
  }, [persist, toast]);

  const value = useMemo(() => ({
    isSaved,
    isSaving,
    toggleSaved,
    setSavedState,
    toast,
    dismissToast: () => setToast(null),
    undoToastAction,
  }), [isSaved, isSaving, setSavedState, toast, toggleSaved, undoToastAction]);

  return <SavedOpportunitiesContext.Provider value={value}>{children}</SavedOpportunitiesContext.Provider>;
};
